import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const ManualMatchModal = ({ isOpen, onClose, song, onMatch }) => {
  const [searchQuery, setSearchQuery] = useState(`${song?.title} ${song?.artist}`);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  // Mock search results
  const mockSearchResults = [
    {
      id: 'match1',
      title: song?.title || 'Song Title',
      artist: song?.artist || 'Artist Name',
      album: 'Album Name',
      duration: 210,
      confidence: 95,
      albumArt: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop',
      platform: 'YouTube Music'
    },
    {
      id: 'match2',
      title: `${song?.title || 'Song Title'} (Remastered)`,
      artist: song?.artist || 'Artist Name',
      album: 'Remastered Album',
      duration: 215,
      confidence: 88,
      albumArt: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=100&h=100&fit=crop',
      platform: 'YouTube Music'
    },
    {
      id: 'match3',
      title: song?.title || 'Song Title',
      artist: `${song?.artist || 'Artist Name'} feat. Another Artist`,
      album: 'Collaboration Album',
      duration: 205,
      confidence: 82,
      albumArt: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop',
      platform: 'YouTube Music'
    }
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    // Simulate API call
    setTimeout(() => {
      setSearchResults(mockSearchResults);
      setIsSearching(false);
    }, 1000);
  };

  const handleMatch = () => {
    if (selectedMatch) {
      onMatch(song.id, selectedMatch);
      onClose();
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    return 'text-error';
  };

  if (!isOpen || !song) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Search" size={24} className="text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Manual Song Match</h2>
                <p className="text-sm text-text-secondary">Find the correct match for this song</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-surface-secondary transition-colors duration-200"
            >
              <Icon name="X" size={20} className="text-text-muted" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Original Song Info */}
          <div className="mb-6 p-4 bg-background border border-border rounded-lg">
            <h3 className="text-sm font-medium text-text-primary mb-3">Original Song</h3>
            <div className="flex items-center space-x-3">
              <Image
                src={song.albumArt || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop'}
                alt={`${song.title} album art`}
                className="w-12 h-12 rounded-lg object-cover"
              />
              <div>
                <p className="font-medium text-text-primary">{song.title}</p>
                <p className="text-sm text-text-secondary">{song.artist}</p>
                <p className="text-xs text-text-muted">{song.album} • {formatDuration(song.duration)}</p>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-text-primary mb-3">Search for Match</h3>
            <div className="flex space-x-3">
              <Input
                type="text"
                placeholder="Search by title, artist, or album..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button
                variant="primary"
                iconName="Search"
                iconSize={16}
                onClick={handleSearch}
                loading={isSearching}
                disabled={!searchQuery.trim()}
              >
                Search
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-text-primary">Search Results</h3>
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedMatch?.id === result.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-surface-secondary'
                  }`}
                  onClick={() => setSelectedMatch(result)}
                >
                  <div className="flex items-center space-x-3">
                    <Image
                      src={result.albumArt}
                      alt={`${result.title} album art`}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-text-primary">{result.title}</p>
                          <p className="text-sm text-text-secondary">{result.artist}</p>
                          <p className="text-xs text-text-muted">
                            {result.album} • {formatDuration(result.duration)} • {result.platform}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${getConfidenceColor(result.confidence)}`}>
                            {result.confidence}% match
                          </p>
                          {selectedMatch?.id === result.id && (
                            <Icon name="CheckCircle" size={16} className="text-primary mt-1" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {searchResults.length === 0 && searchQuery && !isSearching && (
            <div className="text-center py-8">
              <Icon name="Search" size={48} className="text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No matches found for "{searchQuery}"</p>
              <p className="text-sm text-text-muted mt-1">Try adjusting your search terms</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              iconName="X"
              iconSize={16}
              onClick={onClose}
            >
              Skip This Song
            </Button>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                iconName="Check"
                iconSize={16}
                onClick={handleMatch}
                disabled={!selectedMatch}
              >
                Confirm Match
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualMatchModal;